/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import clsx from 'clsx';
// Lib
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const Feature = (props)=>{
    const isEditing = useIsEditing();
    const id = props?.params?.RenderingIdentifier;
    const styles = props?.params?.Styles ?? '';
    const theme = props?.params?.selectTheme;
    const alignment = props?.params?.alignment || 'Left'; // Default: Left
    const { eyebrow, subHeadline, background, icon, headline, description, cta1Link, cta2Link } = props?.fields || {};
    const { base, wrapper, imageWrapper, bgImage, backgroundImageOverlay, contentWrapper, eyebrowWrapper, eyebrowText, iconWrapper, titleWrappers, subHeadlineText, headlineText, descriptionText, actionButtonWrapper } = TAILWIND_VARIANTS({
        alignment: alignment
    });
    /*
   * Rendering
   */ return /*#__PURE__*/ _jsx("section", {
        className: clsx(base(), styles),
        "data-component": "authorable/shared/content/feature",
        ...editingHydrationProps(isEditing),
        id: id ? id : undefined,
        ...getTestProps(`component-feature-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
            theme: theme,
            children: /*#__PURE__*/ _jsxs("div", {
                className: wrapper(),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: clsx(imageWrapper()),
                        children: [
                            background?.value?.src && /*#__PURE__*/ _jsx(ImageWrapper, {
                                className: clsx(bgImage(), background?.value?.src ? 'opacity-15' : 'opacity-0'),
                                field: background,
                                layout: "fill",
                                ...getTestProps(`image`)
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: clsx(backgroundImageOverlay())
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: contentWrapper(),
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: eyebrowWrapper(),
                                children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    className: clsx(eyebrowText()),
                                    field: eyebrow,
                                    tag: "span",
                                    ...getTestProps(`eyebrow`)
                                })
                            }),
                            icon?.value?.src && /*#__PURE__*/ _jsx("div", {
                                className: clsx(iconWrapper()),
                                children: /*#__PURE__*/ _jsx(ImageWrapper, {
                                    field: icon,
                                    ...getTestProps(`icon`)
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: titleWrappers(),
                                children: [
                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        className: clsx(headlineText()),
                                        field: headline,
                                        tag: "h2",
                                        ...getTestProps(`headline`)
                                    }),
                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        className: clsx(subHeadlineText()),
                                        field: subHeadline,
                                        tag: "h3",
                                        ...getTestProps(`sub-headline`)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(RichTextWrapper, {
                                className: clsx(descriptionText()),
                                field: description,
                                ...getTestProps(`description`)
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: actionButtonWrapper(),
                                children: [
                                    /*#__PURE__*/ _jsx(LinkWrapper, {
                                        ctaVariant: "primary",
                                        field: cta1Link,
                                        ctaComponentClass: "component-feature-button-color-1",
                                        ...getTestProps(`cta-1`)
                                    }),
                                    /*#__PURE__*/ _jsx(LinkWrapper, {
                                        ctaVariant: "secondary",
                                        field: cta2Link,
                                        ctaComponentClass: "component-feature-button-color-2",
                                        ...getTestProps(`cta-2`)
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        })
    });
};
export const Default = withStandardComponentWrapper(Feature);
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'relative',
            'w-full'
        ],
        wrapper: [
            'py-component-feature-container-copy-container-padding-y',
            'px-component-feature-container-copy-container-padding-x',
            'relative',
            'min-h-height-minHeight-header-minHeight',
            'rounded-general-border-radius-container',
            'overflow-hidden'
        ],
        imageWrapper: [
            'absolute',
            'inset-0',
            'z-0'
        ],
        bgImage: [
            'z-0',
            'object-cover',
            'h-full',
            'w-full'
        ],
        backgroundImageOverlay: [
            'absolute',
            'inset-0',
            '-z-10',
            'bg-component-feature-bg'
        ],
        contentWrapper: [
            'relative',
            'flex',
            'flex-col',
            'lg:max-w-typography-copy-max-Width'
        ],
        eyebrowWrapper: [
            'pb-general-spacing-eyebrow-margin-bottom',
            'text-component-hero-eyebrow'
        ],
        eyebrowText: [
            'font-typography-font-family-header',
            'text-typography-font-size-xsmall',
            'font-bold',
            'leading-normal',
            'tracking-[0.16875rem] uppercase',
            'text-component-hero-eyebrow'
        ],
        iconWrapper: [
            'iconWrapper',
            'pb-margin-less',
            'flex'
        ],
        titleWrappers: [
            'headingWrapper',
            'flex',
            'flex-col',
            'gap-general-spacing-title-margin-bottom',
            'pb-general-spacing-subtitle-margin-bottom',
            'text-component-feature-subtitle'
        ],
        subHeadlineText: [
            'text-typography-font-size-medium',
            'font-typography-font-family-header',
            'font-bold',
            'leading-normal'
        ],
        headlineText: [
            'font-typography-font-family-header',
            'text-typography-font-size-xlarge',
            'text-component-feature-title',
            'font-bold',
            'leading-none'
        ],
        descriptionText: [
            'font-typography-font-family-body',
            'font-normal',
            'leading-normal',
            'text-component-feature-body',
            'text-typography-font-size-medium'
        ],
        actionButtonWrapper: [
            'flex',
            'flex-col',
            'md:flex-row',
            'gap-space-between-less',
            'mt-general-spacing-copy-margin-bottom',
            'pt-general-spacing-buttons-margin-top'
        ]
    },
    variants: {
        alignment: {
            Left: {
                contentWrapper: [
                    'justify-start',
                    'text-left'
                ],
                actionButtonWrapper: [
                    'justify-start'
                ],
                iconWrapper: [
                    'justify-start'
                ]
            },
            Center: {
                contentWrapper: [
                    'justify-center',
                    'text-center',
                    'm-auto'
                ],
                actionButtonWrapper: [
                    'justify-center'
                ],
                iconWrapper: [
                    'justify-center'
                ],
                descriptionText: [
                    'text-center'
                ]
            },
            Right: {
                contentWrapper: [
                    'justify-end',
                    'text-right'
                ],
                actionButtonWrapper: [
                    'justify-end'
                ],
                iconWrapper: [
                    'justify-end'
                ],
                descriptionText: [
                    'text-right'
                ]
            }
        }
    }
});
